<template>
  <PageHeader :title="target?.name ?? 'My profile'">
    <template #status v-if="isSelf && currentPerson">
      <v-chip
        :color="currentPerson.activeMembershipTypeName ? undefined : 'warning'"
      >
        {{ currentPerson.activeMembershipTypeName ? "Active" : "Inactive" }}
      </v-chip>
      <v-chip v-if="currentPerson.allowSignouts === false" color="warning">
        <v-tooltip
          text="Your account is on an administrative hold. Contact Guest Services for more information."
          >Admin Hold</v-tooltip
        >
      </v-chip>
    </template>
    <template #actions>
      <v-select
        v-if="household.length > 1"
        :model-value="targetId"
        :items="household"
        item-title="name"
        item-value="personId"
        label="Household member"
        density="compact"
        hide-details
        style="min-width: 220px"
        data-testid="household-switcher"
        @update:model-value="selectPerson"
      />
      <v-dialog
        v-model="editing"
        max-width="1000"
        scrollable
        :persistent="editing"
      >
        <template #activator="{ props: activatorProps }">
          <v-btn
            v-if="target"
            v-bind="activatorProps"
            color="primary"
            prepend-icon="mdi-account-edit"
            >Edit</v-btn
          >
        </template>
        <v-card title="Edit profile">
          <v-card-text>
            <MemberProfileForm
              v-if="editing && targetId"
              :key="targetId"
              :person-id="targetId"
              tabbed
              @saved="saved"
              @cancel="editing = false"
            />
          </v-card-text>
        </v-card>
      </v-dialog>
    </template>
  </PageHeader>
  <v-container>
    <v-alert v-if="loadError" type="error" role="alert">
      {{ loadError }} <v-btn @click="load">Retry</v-btn>
    </v-alert>
    <v-progress-linear v-else-if="!target" indeterminate />
    <template v-else>
      <v-card class="pa-6">
        <template #title>
          <div class="d-flex flex-wrap align-center ga-4">
            <span>Profile</span>
            <div
              v-if="assignedPersonTypes.length || eligiblePersonTypes.length"
              class="d-flex flex-wrap gap-2 align-center"
              data-testid="profile-person-type-badges"
            >
              <div
                v-if="assignedPersonTypes.length"
                class="d-flex flex-wrap align-center mr-4"
              >
                <span class="text-caption text-medium-emphasis mr-2"
                  >Assigned:</span
                >
                <v-chip
                  v-for="typeKey in assignedPersonTypes"
                  :key="'profile-assigned-' + typeKey"
                  color="primary"
                  size="small"
                  class="mr-1 mb-1"
                >
                  {{ PERSON_TYPE_LABELS[typeKey] ?? typeKey }}
                </v-chip>
              </div>

              <div
                v-if="eligiblePersonTypes.length"
                class="d-flex flex-wrap align-center"
              >
                <span class="text-caption text-medium-emphasis mr-2"
                  >Eligible:</span
                >
                <v-chip
                  v-for="typeKey in eligiblePersonTypes"
                  :key="'profile-eligible-' + typeKey"
                  color="success"
                  variant="outlined"
                  size="small"
                  class="mr-1 mb-1"
                >
                  {{ PERSON_TYPE_LABELS[typeKey] ?? typeKey }}
                </v-chip>
              </div>
            </div>
          </div>
        </template>

        <Suspense v-if="isSelf && currentPerson"
          ><PersonSummaryDetails :person="currentPerson"
        /></Suspense>
        <p v-else-if="target.email">{{ target.email }}</p>
      </v-card>
      <PortalPersonTabs
        :key="`${target.personId}-${refreshKey}`"
        :person="target"
        :is-self="isSelf"
        :is-junior="isJunior"
      />
    </template>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onScopeDispose, ref, shallowRef, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useUser } from "@/composables/useUser";
import { useMemberPersonId } from "@/composables/people/useMemberPersonId";
import { errorMessage } from "@/composables/generated/contracts";
import { upperSnakeToTitleCase } from "@/api/util";
import PortalPersonTabs from "@/components/portal/PortalPersonTabs.vue";
import MemberProfileForm from "@/components/portal/MemberProfileForm.vue";
import type { MemberProfile } from "../../../cbi-generator/generated/dto/MemberProfile";
import type { PersonMember } from "../../../cbi-generator/generated/dto/PersonMember";
import { usePersonService } from "../../../cbi-generator/frontend/composables/usePersonFrontendService";
import { usePersonTypeRulesService } from "../../../cbi-generator/frontend/composables/usePersonTypeRulesFrontendService";
import type { PersonTypeMask } from "../../../cbi-generator/generated/dto/PersonTypeMask";
import { PersonTypeKey } from "../../../cbi-generator/generated/dto/PersonTypeKey";

const auth = useAuthStore();
const userStore = useUser();
const currentPerson = userStore.currentPersonRef;
const selfId = useMemberPersonId();
const route = useRoute();
const router = useRouter();
const service = usePersonService();
const rules = usePersonTypeRulesService();
const masks = shallowRef<PersonTypeMask[]>([]);

const PERSON_TYPE_LABELS: Record<string, string> = {
  [PersonTypeKey.ADULT_MEMBER]: "Adult Member",
  [PersonTypeKey.JUNIOR_MEMBER]: "Junior Member",
  [PersonTypeKey.UAP_MEMBER]: "UAP Member",
  [PersonTypeKey.ORGANIZATION]: "Organization",
  [PersonTypeKey.ORGANIZATION_CONTACT]: "Organization Contact",
  [PersonTypeKey.FRIEND]: "Friend",
  [PersonTypeKey.JUNIOR_PARENT]: "Junior Parent",
  [PersonTypeKey.ADULT_GUEST]: "Adult Guest",
  [PersonTypeKey.JP_INSTRUCTOR]: "JP Instructor",
  [PersonTypeKey.EMERGENCY_CONTACT]: "Emergency Contact",
};

const DISPLAYED_ELIGIBLE_TYPES = new Set<string>([
  PersonTypeKey.ADULT_MEMBER,
  PersonTypeKey.JUNIOR_PARENT,
]);

const assignedPersonTypes = computed<string[]>(() => {
  const result = new Set<string>();
  if (profile.value?.personType) {
    result.add(profile.value.personType);
  }
  const pluralList = profile.value?.person?.personTypes ?? [];
  for (const pt of pluralList) {
    const typeId = pt.typeIdTargetIds?.typeId;
    if (typeId != null) {
      const match = masks.value.find((m) => m.personTypeId?.typeId === typeId);
      if (match?.personType) {
        result.add(match.personType);
      }
    }
  }
  return Array.from(result);
});

const isJunior = computed(() =>
  assignedPersonTypes.value.includes(PersonTypeKey.JUNIOR_MEMBER),
);

const eligiblePersonTypes = computed<string[]>(() => {
  if (!profile.value || !masks.value.length) return [];
  const p = profile.value.person;
  const addressCount = profile.value.addresses.length;
  const phoneCount = profile.value.phones.length;
  const emergencyCount = profile.value.emergencyContacts.length;

  return masks.value
    .filter((m) => {
      if (!DISPLAYED_ELIGIBLE_TYPES.has(m.personType)) return false;
      if (addressCount < m.minAddresses) return false;
      if (phoneCount < m.minPhones) return false;
      if (emergencyCount < m.minEmergencyContacts) return false;
      for (const field of m.requiredFields) {
        const val = (p as unknown as Record<string, unknown>)[field];
        if (field === "dob") {
          if (!val) return false;
        } else if (typeof val === "string") {
          if (!val.trim()) return false;
        } else if (val == null) {
          return false;
        }
      }
      return true;
    })
    .map((m) => m.personType);
});
const targetId = computed(() => {
  const raw = route.params.personId;
  return raw ? Number(Array.isArray(raw) ? raw[0] : raw) : selfId.value;
});
const isSelf = computed(() => targetId.value === selfId.value);
const household = shallowRef<PersonMember[]>([]);
const profile = shallowRef<MemberProfile>();
const target = computed(() => profile.value?.person);
const editing = ref(false);
const loadError = ref("");
const refreshKey = ref(0);
let version = 0;
onScopeDispose(() => ++version);
async function load() {
  const current = ++version;
  profile.value = undefined;
  household.value = [];
  editing.value = false;
  loadError.value = "";
  if (selfId.value == null) return;
  if (!Number.isSafeInteger(targetId.value) || targetId.value! <= 0) {
    loadError.value = "Invalid person profile.";
    return;
  }
  try {
    const [loaded, self, maskDefs] = await Promise.all([
      service.getProfileMember({ personId: targetId.value! }),
      service.getPersonMember(selfId.value),
      masks.value.length ? Promise.resolve(masks.value) : rules.masksMember(),
    ]);
    const children = await self.getChildren();
    if (current !== version) return;
    masks.value = maskDefs;
    profile.value = loaded;
    household.value = [self, ...children];
  } catch (cause) {
    if (current === version) loadError.value = errorMessage(cause);
  }
}
watch([targetId, selfId], load, { immediate: true });
function selectPerson(id: number) {
  router.push({
    name: "Person Profile",
    params: { personId: id === selfId.value ? undefined : id },
  });
}
function saved(value: MemberProfile) {
  profile.value = value;
  household.value = household.value.map((p) =>
    p.personId === value.person.personId ? value.person : p,
  );
  refreshKey.value++;
  editing.value = false;
  const current = version;
  void userStore.loadUser(true).catch((cause) => {
    if (current === version)
      loadError.value = `Profile saved, but refreshing the account summary failed: ${errorMessage(cause)}`;
  });
}
</script>
